// ===== Request =====

import { TimesheetAdditionForLeaveModel } from "@/app/models/timesheet/groupedTimesheetModel"

export type GetPayrollDriversQuery = {
  companyId: string
  // Grouped timesheet is fetched for the drivers selected in the side panel,
  // mirroring the timesheet view's TimesheetService.getGroupedTimesheets call.
  driversIds: number[]
  year: number
  month: number // 1-12
  showBonusConfiguration?: boolean
  domesticBusinessTripDateType?: 'start' | 'end'
  // Legacy group-based fields (kept optional for backward compatibility).
  groupFilterStatus?: 'active' | 'inactive'
  groupFilterId?: number
  take?: number
  skip?: number
  phrase?: string
}

export type SortKey =
  | 'select'
  | 'fullName'
  | 'bailiffDeduction'
  | 'offPay'
  | 'sickPay'
  | 'benefits'
  | 'costs'
  | 'businessTrip'
  | 'expectedNetto'
  | 'calculatedNetto'
  | 'reward'
  | 'summary'

export type GetPayrollPagedDriversQuery = {
  groupFilterStatus?: 'active' | 'inactive'
  groupFilterId?: number
  companyId: string
  domesticBusinessTripDateType?: 'start' | 'end'
  year: number
  month: number // 1-12
  take?: number
  skip?: number
  phrase: string,
  sortBy: SortKey,
  sortDir: 'asc'|'desc',
  // cache control
  lastSavedDate: string,
}

// ===== EasyTMS expected netto =====

export type FetchExpectedNettoFromEasyTmsRequest = {
  companyId: string
  year: number
  month: number
  driverIds: number[]
}

export type ExpectedNettoFromEasyTmsRow = {
  driverId: number
  akronim: string | null
  expectedNettoAmount: number | null
  found: boolean
}

export type FetchExpectedNettoFromEasyTmsResponse = ExpectedNettoFromEasyTmsRow[]

export type FetchMandatesAdvancesFromEasyTmsRequest = {
  companyId: string
  year: number
  month: number
  driverIds: number[]
}

export type MandatesAdvancesFromEasyTmsRow = {
  driverId: number
  akronim: string | null
  /** Fines total for the month, in PLN. */
  mandatesSum: number | null
  /** Advances total for the month converted to PLN (may be negative). */
  advancesSumPln: number | null
  found: boolean
}

export type FetchMandatesAdvancesFromEasyTmsResponse =
  MandatesAdvancesFromEasyTmsRow[]

// ===== Response =====

export type PayrollDriversResponse = PayrollDriverRow[]

export type PayrollPagedDriversResponse = {
  drivers: PayrollDriverRow[]
  total: number
}

export type PayrollDriverRow = {
  driver: DriverDto
  calculations: boolean[] // length 12
  hasContract: number[] // length 12 (np. 0/3)

  /** BRUTTO (LACZNIE) - GroupedTimesheetDto.Summary, czyli decimal? na serwerze. */
  summary: number | null

  expectedNettoAmount: number | null
  calculatedBonus: number | null
  calculatedNetto: number | null
  polishTripsAmount: number | null
  isNettoAmountModifiedByUser: boolean | null

  reward: number | null
  bonus: number | null

  leavesGrossAmount: number | null
  sickLeavesGrossAmount: number | null
  leavesNettoAmount: number | null
  sickLeavesNettoAmount: number | null
  finalLeavesGrossAmount: number | null
  finalLeavesGrossHours: number | null

  bailiffDeductionAmount: number | null
  finalNettoAmount: number | null

  additionAmount: number | null
  deductionAmount: number | null
  tripCosts: number | null

  useValuesFromContract: boolean
  /**
   * Ewidencja jest policzona, ale dane zrodlowe (delegacje, aktywnosci, typy dni)
   * zmienily sie po ostatnim przeliczeniu - kwoty w wierszu sa migawka sprzed zmiany.
   */
  isOutOfDate: boolean
  /** Co uniewaznilo miesiac - patrz TimesheetModificationType. */
  modificationType: TimesheetModificationType
  lastCalculationDate: string | null // ISO date-time? (na razie string)

  timesheetAdditionForLeaves: TimesheetAdditionDto[]

  hasBonusConfiguration: boolean
  nettoAmountComponent: NettoAmountComponentDto | null

  benefitsGrossAmount: null | number
  benefitsNettoAmount: null | number

  includeDomesticBusinessTrip: boolean | null
}

export type DriverDto = {
  name: string
  surname: string
  fullName: string
  id: number

  nationality: string | null
  email: string | null
  workingStartTime: string | null // unknown format -> string
  isActive: boolean

  driverCard: unknown | null
  driverCards: unknown | null

  cardValidTo: string | null // ISO date? -> string
  cardNumber: string | null
  phoneNumber: string | null

  firstContractStartDate: string | null // ISO date? -> string
}

export type TimesheetAdditionDto = {
  dayType: number // np. 6
  amount: number
  // Wpis wyliczony przez silnik (do wyświetlenia). Tylko wpisy z flagą false
  // (edycje użytkownika) są traktowane przy przeliczeniu jako ręczne kwoty.
  isAutoGeneratedAddition?: boolean
}

export type NettoAmountComponentDto = Record<string, unknown>

export type UpdateGroupedTimesheetMutationProps = {
  year: number
  month: number
  companyId: string | number
}

export type UpdateGroupedTimesheetMutationPayload = {
  drivers: {
    id: number
    bonusConfiguration: {
      // Fields handled in the new API flow (values existed before, but are now sent explicitly)
      expectedNettoAmount: number
      leavesGrossAmount: number
      sickLeavesGrossAmount: number

      // New fields introduced in the new API (not present in the old API)
      includeDomesticBusinessTrip?: boolean
      benefitsGrossAmount?: number

      // Legacy fields from the old bonus configuration.
      // In the new API these should be resolved by the backend automatically,
      // but they are kept here for backward compatibility.
      bailiffDeductionAmount?: number
      bonusRate?: number
      driverId?: string | number
      leavesNettoAmount?: number
      rewardRate?: number
      sickLeavesNettoAmount?: number
      timesheetAdditionForLeaves?: Array<TimesheetAdditionForLeaveModel>
      useValuesFromContract?: boolean
    }
  }[]
}


    //bonusConfigurations: any[] //@TODO
    //companyId: string
    //drivers: number[]
    //id: string
    //months: number
    //outOfDateQuarterExchangeRateAccepted: boolean
    //year: number

/**
 * Odpowiednik serwerowego TimesheetPartModificationEnum: powod, dla ktorego przeliczona
 * ewidencja stala sie nieaktualna. Kolejnosc musi zostac zgodna z backendem.
 */
export enum TimesheetModificationType {
  None = 0,
  Activities = 1,
  DayType = 2,
  Schedule = 3,
  BusinessTrips = 4,
  Contract = 5,
  Configuration = 6,
}
